// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import React from 'react'
import DownloadVideo from 'components/DownloadVideo'
import Map from 'components/map/Map'
import { connect } from 'react-redux'
import { DOWNLOAD_TASK_TYPE } from '../../constants'
import { Row, Col, message } from 'antd'
import CameraFinder from 'components/camera-finder'
import { MultipleSelectionModel } from 'utils/multiple-selection-model'
import GPSTracker from 'components/map/gps-tracker'
import { debounce } from 'lodash'
import classnames from 'classnames'
import './index.less'
import { withRouter } from 'react-router-dom'
import { getRoadName } from '../../api/devices'

class ImageryUpload extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      layers: []
    }
    this.map = null
    this.timeTaskId = null
    this.selectionModel = new MultipleSelectionModel(0, 'Single')
    this.selectionModel.emitter.on('change', this.handleFinderSelectionChange)
  }

  timeoutHandler = null

  async componentWillMount () {
    const historyState = this.props.location.state || {}
    if (historyState.cityID !== undefined) {
      await this.props.changeCity(historyState.cityID)
    }
    if (historyState.taskType !== undefined) {
      await this.props.changeTaskType(historyState.taskType)
    } else {
      await this.props.changeTaskType(DOWNLOAD_TASK_TYPE.DEVICE)
    }
    if (historyState.taskType === DOWNLOAD_TASK_TYPE.DEVICE && historyState.deviceID) {
      this.props.setMapCenter([historyState.longitude, historyState.latitude])
    }
  }

  componentDidMount () {
    this.props.setDefaultCenter()
  }

  componentWillUnmount () {
    const {
      clearDownloadState,
      clearRoadState,
      clearGeofenceState,
      clearCameraFinderState,
      clearCameraState,
      clearMapState
    } = this.props
    if (this.timeTaskId) {
      clearInterval(this.timeTaskId)
    }
    this.selectionModel.dispose()
    clearDownloadState()
    clearRoadState()
    clearGeofenceState()
    clearCameraFinderState()
    clearCameraState()
    clearMapState()
  }

  componentDidUpdate (prevProps) {
    if (prevProps.cameraList !== this.props.cameraList) {
      this.updateSelectionModel(prevProps.cameraList, this.props.cameraList)
    }
  }

  updateSelectionModel = (oldCameraList, latestCameraList) => {
    const oldSelectedIndices = this.selectionModel.getSelectedIndices()
    const selectedItems = oldSelectedIndices.map(index => oldCameraList[index])
    const selectedIndices = []
    for (let i = 0; i < latestCameraList.length; i++) {
      for (let k = 0; k < selectedItems.length; k++) {
        if (latestCameraList[i].deviceID === selectedItems[k].deviceID) {
          selectedIndices.push(i)
          continue
        }
      }
    }
    this.selectionModel.setItemCount(latestCameraList ? latestCameraList.length : 0)
    if (selectedIndices.length > 0) {
      this.selectionModel.selectIndices(...selectedIndices)
    } else {
      this.selectionModel.clearAll()
    }
  }

  handleFinderSelectionChange = selectedIndices => {
    const { cameraList, onCameraClick } = this.props
    const selectedDevices = selectedIndices.map(i => cameraList[i])
    if (selectedDevices.length > 0) {
      onCameraClick(selectedDevices[0])
    }
  }

  handleCameraHover (camera) {
    const { updateHoveredCamera,hoveredCamera } = this.props
    if (camera) {
      if (this.timeoutHandler) {
        clearTimeout(this.timeoutHandler)
      }
      //timeout used to hovering clustered cameras more smoothly
      this.timeoutHandler = setTimeout(async () => {
        let roadName = await getRoadName(camera.deviceID)
        camera.road = roadName
        updateHoveredCamera(camera)
      }, 200)
    }
  }

  handleMapClick (e) {
    const { updateHoveredCamera, hoveredCamera } = this.props
    if (hoveredCamera) {
      updateHoveredCamera(null)
    }
  }

  getGPSTrace = async deviceId => {
    const { setActiveTraceDevice, isGPSTrackerVisible, fetchAndUpdateDeviceTrace } = this.props
    if (isGPSTrackerVisible) {
      await setActiveTraceDevice(deviceId)
      fetchAndUpdateDeviceTrace()
    }
  }

  handleMapLoaded = async () => {
    await this.handleCameraFilter()
    await this.initPageByHistoryStateAfterMapLoaded()
  }

  async initPageByHistoryStateAfterMapLoaded () {
    const historyState = this.props.location.state || {}
    if (historyState.taskType === DOWNLOAD_TASK_TYPE.DEVICE && historyState.deviceID) {
      this.handleCameraIconClick({ deviceID: historyState.deviceID })
    }
  }

  restartInterval = () => {
    if (this.timeTaskId) {
      clearInterval(this.timeTaskId)
    }
    this.timeTaskId = setInterval(this.handleCameraFilter, 1000 * 60)
  }

  handleViewportChanged = () => {
    this.handleCameraFilter()
  }

  handleViewportChangedDebounce = debounce(this.handleViewportChanged, 1000)

  handleUploadTaskClear = () => {
    this.selectionModel.clearAll()
  }

  handleCameraIconClick = ({ deviceID }, event = {}) => {
    const { cameraList, isGPSTrackerVisible } = this.props
    const index = cameraList.findIndex(d => d.deviceID === deviceID)
    this.selectionModel.select(index)
    if (isGPSTrackerVisible) {
      this.getGPSTrace(deviceID)
    }
  }

  handleCameraFilter = async () => {
    const {
      cameraFinderState,
      currentCity,
      boundingBox,
      downloadMode,
      fetchCameras,
      setCameraFinderLoading
    } = this.props
    const query = {
      cityID: currentCity.id,
      bbox: boundingBox
    }
    if (cameraFinderState.filter.onlineStatus > -1) {
      query.onlineStatus = cameraFinderState.filter.onlineStatus
    }
    if (cameraFinderState.filter.status > -1) {
      query.status = cameraFinderState.filter.status
    }
    if (cameraFinderState.filter.deviceID) {
      query.keyword = cameraFinderState.filter.deviceID
      delete query.bbox
    }
    query.page = 1
    query.pageSize = 10000
    setCameraFinderLoading(true)
    await fetchCameras(query)
    setCameraFinderLoading(false)
    this.restartInterval()
  }

  render () {
    const {
      isDownloadShow,
      selectedCameraID,
      isMapReady,
      isGeoFenceVisible,
      cameraList,
      isCameraFinderVisible,
      isGPSTrackerVisible,
      isCameraFinderExpanded
    } = this.props
    const mapContainerStyle = { height: 'calc(100vh - 40px)' }
    const col1Width = isDownloadShow ? 320 : 1
    const finderClassNames = classnames('finder-popup', {
      'finder-popup-expanded': isCameraFinderExpanded
    })
    return (
      <Row style={{ height: '100%' }} className='imagery-upload'>
        <Col style={{ flexBasis: col1Width, height: '100%' }}>
          <DownloadVideo loading={!isMapReady} onClear={this.handleUploadTaskClear} />
        </Col>
        <Col style={{ flex: 'auto', height: '100%' }}>
          <Map
            isGeoFenceVisible={isGeoFenceVisible}
            containerStyle={mapContainerStyle}
            onMapClick={this.handleMapClick.bind(this)}
            onCameraHover={this.handleCameraHover.bind(this)}
            selectedCameraID={selectedCameraID}
            onCameraClick={this.handleCameraIconClick}
            onStyleLoad={this.handleMapLoaded}
            onViewportChanged={this.handleViewportChangedDebounce}
          />
          <div className={finderClassNames}>
            {isCameraFinderVisible && (
              <CameraFinder
                style={isGPSTrackerVisible ? { maxHeight: 'calc(100vh - 170px)' } : { borderRadius: 4 }}
                items={cameraList}
                selectionModel={this.selectionModel}
                onFilter={this.handleCameraFilter}
              />
            )}
            <GPSTracker
              style={
                isCameraFinderExpanded
                  ? { borderTop: '1px solid #ccd6dd', borderRadius: 0 }
                  : { marginTop: 8, marginRight: 8 }
              }
            />
          </div>
        </Col>
      </Row>
    )
  }
}

export default withRouter(
  connect(
    state => ({
      selectedCameraID: state.download.editingPreTask.deviceID,
      hoveredCamera: state.cameras.hoveredCamera,
      isMapReady: !!state.map.boundingBox.length,
      isGeoFenceVisible: state.download.editingPreTask.jobType === DOWNLOAD_TASK_TYPE.GEOFENCE,
      isDownloadShow: state.download.isDownloadShow,
      downloadMode: state.download.mode,
      menuCollapsed: state.app.menuCollapsed,
      cameraList: state.cameras.cameraList,
      isCameraFinderVisible: state.cameraFinder.visible,
      isCameraFinderExpanded: state.cameraFinder.expanded,
      isGPSTrackerVisible: state.gpsTrace.isGPSTrackerVisible,
      boundingBox: state.map.boundingBox,
      currentCity: state.city.current,
      cameraFinderState: state.cameraFinder,
      editingPreTask: state.download.editingPreTask
    }),
    dispatch => ({
      fetchCityList: dispatch.city.fetchCityList,
      fetchCameras: dispatch.cameras.fetchCameras,
      onCameraClick: dispatch.download.onCameraClick,
      fetchCameraDetails: dispatch.cameras.fetchCameraDetails,
      updateHoveredCamera: dispatch.cameras.updateHoveredCamera,
      setActiveTraceDevice: dispatch.gpsTrace.setActiveDevice,
      fetchAndUpdateDeviceTrace: dispatch.gpsTrace.fetchAndUpdateDeviceTrace,
      setCameraFinderLoading: dispatch.cameraFinder.setLoading,
      setCameraFinderVisible: dispatch.cameraFinder.setVisible,
      updateEditingPreTask: dispatch.download.updateEditingPreTask,
      clearDownloadState: dispatch.download.clear,
      clearRoadState: dispatch.road.clear,
      clearGeofenceState: dispatch.geofence.clear,
      clearCameraFinderState: dispatch.cameraFinder.clear,
      clearCameraState: dispatch.cameras.clear,
      clearMapState: dispatch.map.clear,
      setDefaultCenter: dispatch.map.setDefaultCenter,
      setMapCenter: dispatch.map.setCenter,
      changeCity: dispatch.city.onCityChange,
      changeTaskType: dispatch.download.changeTaskType
    })
  )(ImageryUpload)
)
